import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import notificationService, { Notification as NotificationData } from '../services/notificationService';
import { useWebSocket } from '../hooks/useWebSocket';

const NotificationsPage: React.FC = () => {
    const [notifications, setNotifications] = useState<NotificationData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { user, token } = useAuth();
    const { onNewPost, onNewNotification } = useWebSocket();

    useEffect(() => {
        fetchNotifications();
    }, []);

    useEffect(() => {
        // Listen for real-time notifications
        const unsubscribeFromNewPost = onNewPost((data) => {
            console.log('🔔 New post notification received:', data);
            // Show toast notification
            if (window.Notification && Notification.permission === 'granted') {
                new window.Notification('New Content!', {
                    body: data.message,
                    icon: '/favicon.ico'
                });
            }
            // Refresh notifications to show the new one
            fetchNotifications();
        });

        const unsubscribeFromNotification = onNewNotification((data) => {
            console.log('🔔 New general notification received:', data);
            // Refresh notifications
            fetchNotifications();
        });

        // Request notification permission if not already granted
        if (window.Notification && Notification.permission === 'default') {
            Notification.requestPermission();
        }

        return () => {
            unsubscribeFromNewPost();
            unsubscribeFromNotification();
        };
    }, [onNewPost, onNewNotification]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await notificationService.getNotifications();
            console.log('🔔 Notification response:', response);
            response.notifications.forEach((notif, index) => {
                console.log(`🔔 Notification ${index + 1}:`, {
                    id: notif.id,
                    actor: notif.actor,
                    avatar: notif.actor?.avatar
                });
            });
            setNotifications(response.notifications);
        } catch (error: any) {
            setError(error.message || 'Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (notificationId: string) => {
        try {
            await notificationService.markAsRead(notificationId);
            setNotifications(prev => 
                prev.map(notif => 
                    notif.id === notificationId 
                        ? { ...notif, isRead: true }
                        : notif
                )
            );
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications(prev => 
                prev.map(notif => ({ ...notif, isRead: true }))
            );
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'subscription': return 'feather-user-plus';
            case 'like': return 'feather-heart';
            case 'comment': return 'feather-message-circle';
            case 'post': return 'feather-file-text';
            case 'payment': return 'feather-credit-card';
            case 'system': return 'feather-bell';
            default: return 'feather-bell';
        }
    };

    const getNotificationColor = (type: string) => {
        switch (type) {
            case 'subscription': return 'text-success';
            case 'like': return 'text-danger';
            case 'comment': return 'text-primary';
            case 'post': return 'text-info';
            case 'payment': return 'text-warning';
            case 'system': return 'text-secondary';
            default: return 'text-grey-700';
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
        
        if (diffInHours < 1) return 'Just now';
        if (diffInHours < 24) return `${diffInHours}h ago`;
        if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-xss w-100 d-block d-flex border-0 p-4 mb-3">
                        <div className="card-body d-flex align-items-center p-0">
                            <h2 className="fw-700 mb-0 mt-0 font-md text-grey-900">Notifications</h2>
                            {notifications.some(n => !n.isRead) && (
                                <button 
                                    className="btn bg-current text-white font-xsss fw-600 p-2 ms-auto rounded-3"
                                    onClick={markAllAsRead}
                                >
                                    Mark all as read
                                </button>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                            <div className="text-center p-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                            <div className="text-center p-4">
                                <i className="feather-bell font-xl text-grey-500 mb-3"></i>
                                <h4 className="fw-700 text-grey-900">No notifications yet</h4>
                                <p className="text-grey-500 font-xsss">You'll see notifications here when you have them</p>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-xss w-100 d-block border-0 p-0 mb-3">
                            <div className="card-body p-0">
                                {notifications.map((notification) => (
                                    <div 
                                        key={notification.id}
                                        className={`d-flex align-items-center p-4 border-bottom ${!notification.isRead ? 'bg-lightblue' : ''} cursor-pointer`}
                                        onClick={() => !notification.isRead && markAsRead(notification.id)}
                                    >
                                        <div className="position-relative me-3">
                                            <img
                                                src={notification.actor?.avatar || '/assets/images/user.png'}
                                                alt={notification.actor?.username || 'User'}
                                                className="rounded-circle"
                                                style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = '/assets/images/user.png';
                                                }}
                                            />
                                            <div 
                                                className={`position-absolute rounded-circle d-flex align-items-center justify-content-center ${getNotificationColor(notification.type)} bg-white`}
                                                style={{ 
                                                    width: '18px', 
                                                    height: '18px', 
                                                    bottom: '-2px', 
                                                    right: '-2px',
                                                    border: '2px solid white',
                                                    fontSize: '10px'
                                                }}
                                            >
                                                <i className={`${getNotificationIcon(notification.type)}`} style={{ fontSize: '10px' }}></i>
                                            </div>
                                        </div>
                                        <div className="flex-grow-1">
                                            <p className={`font-xsss mb-1 ${!notification.isRead ? 'text-dark' : 'text-grey-700'}`}>
                                                {notification.message}
                                            </p>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span className="font-xssss text-grey-500">
                                                    {formatDate(notification.createdAt)}
                                                </span>
                                                {notification.actor?.username && (
                                                    <span className="font-xssss text-grey-600 fw-600">
                                                        @{notification.actor.username}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {!notification.isRead && (
                                            <div className="bg-current rounded-circle" style={{width: '8px', height: '8px'}}></div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationsPage;
